/**
 * Component Name: ProjectGroupedTable
 * Description: A table component that groups invoices by project and displays aggregated data with sortable columns and clickable project names.
 * Props:
 *   - filteredInvoices (array): List of filtered invoice objects to group by projects.
 *   - projects (array): List of project objects for mapping project IDs to names.
 *   - handleViewDetails (function): Callback to handle invoice detail view clicks.
 *   - handleProjectClick (function): Callback to handle clicks on project names for drill-down.
 * Feature: Displays project-level invoice data, including total invoice amounts, subtotals, and PO counts, with clickable project names to view associated purchase orders in the invoice management interface.
 */
import React from 'react';
import CustomTable from '../table/table';
import { toUSD } from '../../util/currency-formatter';
import { toDecimal, roundValue } from '../../util/decimal-utils';
import idx from 'idx';
import { INVOICE_CSS_CLASSES, INVOICE_TABLE_COLUMNS } from '../../util/enums/invoice-management';
import { GSTORVAT } from '../../util/enums';

const ProjectGroupedTable = ({ filteredInvoices, projects, handleProjectClick }) => {
    // Group invoices by project
    const groupedData = projects.map((project) => {
        const projectInvoices = filteredInvoices.filter((inv) => inv.projectId === project.id);
        const totalInvoiceAmount = projectInvoices.reduce((sum, inv) =>
            sum.plus(toDecimal(inv.totalAmount || 0)), toDecimal(0));
        const totalInvoiceSubtotal = projectInvoices.reduce((sum, inv) =>
            sum.plus(toDecimal(inv.subTotal || 0)), toDecimal(0));

        // Calculate PO Total for the project
        const projectPOs = new Map();
        projectInvoices.forEach(inv => {
            const poId = idx(inv, _ => inv.PurchaseOrders.id);
            if (poId && !projectPOs.has(poId)) {
                projectPOs.set(poId, toDecimal(idx(inv, _ => inv.PurchaseOrders.total) || 0));
            }
        });
        const totalPOTotal = Array.from(projectPOs.values()).reduce((sum, poTotal) =>
            sum.plus(poTotal), toDecimal(0));

        const poCount = new Set(projectInvoices.map((inv) => inv.PurchaseOrders?.id)).size;
        return {
            projectId: project.id,
            projectName: project.name,
            totalInvoiceAmount: roundValue(totalInvoiceAmount),
            totalInvoiceSubtotal: roundValue(totalInvoiceSubtotal),
            invoiceCount: projectInvoices.length,
            totalPOTotal: roundValue(totalPOTotal),
            poCount,
        };
    }).filter((data) => data.invoiceCount > 0); // Only show projects with invoices

    const columns = [
        {
            title: INVOICE_TABLE_COLUMNS.PROJECT_NAME,
            field: 'projectName',
            render: (row) => (
                <span className={INVOICE_CSS_CLASSES.PROJECT_LINK} onClick={() => handleProjectClick(row.projectId, row.projectName)}>
                    {row.projectName}
                </span>
            ),
            sortable: true,
        },
        {
            title: INVOICE_TABLE_COLUMNS.INVOICE_AMOUNT,
            field: 'totalInvoiceAmount',
            class: 'text-right',
            render: (row) => <span className={INVOICE_CSS_CLASSES.AMOUNT}>{toUSD(row.totalInvoiceAmount)}</span>,
            sortable: true,
        },
        {
            title: `${INVOICE_TABLE_COLUMNS.INVOICE_SUBTOTAL} (Ex. ${GSTORVAT})`,
            field: 'totalInvoiceSubtotal',
            class: 'text-right',
            render: (row) => <span className={INVOICE_CSS_CLASSES.SUB_TOTAL_AMOUNT}>{toUSD(row.totalInvoiceSubtotal)}</span>,
            sortable: true,
        },
        {
            title: INVOICE_TABLE_COLUMNS.INVOICE_COUNT,
            field: 'invoiceCount',
            class: 'text-right',
            sortable: true,
        },
        {
            title: INVOICE_TABLE_COLUMNS.PO_TOTAL,
            field: 'totalPOTotal',
            class: 'text-right',
            render: (row) => toUSD(row.totalPOTotal),
            sortable: true,
        },
        {
            title: INVOICE_TABLE_COLUMNS.PO_COUNT,
            field: 'poCount',
            class: 'text-right',
            sortable: true,
        },
    ];

    const tableItemClickHandler = (row) => {
        handleProjectClick(row.projectId, row.projectName);
    };

    return (
        <div className="table-container">
            <CustomTable columns={columns} data={groupedData} pagination={true} rowClickHandler={tableItemClickHandler} />
        </div>
    );
};

export default ProjectGroupedTable;